/* eslint-disable camelcase */
import { findEntityFieldsBySysId } from '../redux/contentful-selectors'
import { contentId } from './contentful'
import { getValue } from './product-schema'

export function getTrackingSchema(state: any, tableProps: any) {
  const { segmentProperties, segmentEvents } = tableProps

  const propertiesComp: GenericJsonModel<SegmentPropertyMappings> = findEntityFieldsBySysId(
    state,
    contentId(segmentProperties),
  )
  const eventsComp: GenericJsonModel<SegmentEventMappings> = findEntityFieldsBySysId(
    state,
    contentId(segmentEvents),
  )
  const properties = propertiesComp.data
  const events = eventsComp.data
  return {
    properties,
    events,
  }
}

/** Wraps nerdalytics call to automatically wait for GA cookie and add them to the event */
export async function callNerdalytics(
  action: string,
  properties: { [key: string]: any },
  callback: () => {} = () => ({}),
  context?: { [key: string]: any },
) {
  if (typeof window !== 'undefined') {
    // TODO: Figure out how to make sure events aren't accidentally triggered server side
    const nerdalytics = (window as any).nerdalytics
    if (nerdalytics) {
      if (window.__APP_ENV__ !== 'development') {
        const t = setTimeout(callback, 1000)
        nerdalytics.track(action, properties, context, () => {
          clearTimeout(t)
          if (callback) {
            callback()
          }
        })
      }
      callback()
    } else {
      // eslint-disable-next-line no-console
      console.warn(`Nerdalytics isn't defined`)
      callback()
    }
  }
}

/** Inject important user input values into the payload
 * so they can be captured in the event if the tracking plan needs it */
/** For a given segment tracking map, finds all the properties using the property mapping to replace their values */
function lookupProperties(
  properties: SegmentEventProperty[],
  propertiesMapping: SegmentPropertyMappings,
  payload: any,
) {
  const resultProps: any = {}
  properties.forEach((prop) => {
    if (typeof prop === 'string') {
      let mapping = propertiesMapping[prop]
      if (!mapping) {
        // eslint-disable-next-line no-console
        console.warn(
          `Property mapping for ${prop} doesn't exist! You should add it to the segment mapping in contentful`,
        )
        mapping = {
          value: prop,
        }
      }
      let value: any = getValue(mapping, payload)
      // Hardcoded handling of string booleans. Later we will add a more generic system of typing per property
      if (value === 'true') {
        value = true
      } else if (value === 'false') {
        value = false
      }
      resultProps[prop] = value
    } else if (typeof prop === 'object') {
      const key = prop.key
      const operationType = prop.type
      const keyOverride = prop.keyOverride
      // By convention, the payload to use should be inside the payload and have the same key as the prop
      const newPayload = payload[key].map((arrayElement: any) => {
        // We need to pass global tracking data through for all array elements in case it's needed
        return { ...arrayElement, trackingData: payload.trackingData }
      })
      // Used for list events, same process but the payload is an array so we repeat it for each element
      if (operationType === 'product_array') {
        let arrayResult = lookupArrayProperties(prop.properties, propertiesMapping, newPayload)
        if (typeof prop.limit === 'number') {
          arrayResult = arrayResult.slice(0, prop.limit)
        }
        resultProps[keyOverride || key] = arrayResult
      }
      // TODO: Implement array mappings for product list events
    }
  })
  return resultProps
}

// Processes an array element in the tracking plan, doing the same process as a normal element but for each payload value
function lookupArrayProperties(
  properties: SegmentEventProperty[],
  propertiesMapping: SegmentPropertyMappings,
  payload: any[],
): any[] {
  const result: any[] = []
  for (const key in payload) {
    // Inject the index of the element in the tracking data because it might be needed
    const trackingData = {
      ...payload[key].trackingData,
      product_position: parseInt(key, 10) + 1,
    }
    const payloadToLookup = { ...payload[key], trackingData }
    const propsForElement = lookupProperties(properties, propertiesMapping, payloadToLookup)
    result.push(propsForElement)
  }
  return result
}

// Typing for the new generic reuseable json contentful model
interface GenericJsonModel<T> {
  name: string
  data: T
}

interface SegmentPropertyMappings {
  [key: string]: SegmentPropertyMap
}

// One of the two will be present
interface SegmentPropertyMap {
  value?: string
  valueFrom?: string
}

type SegmentEventMappings = Array<SegmentEventMap>
interface SegmentEventMap {
  // eslint-disable-next-line camelcase
  event_name: string
  // eslint-disable-next-line camelcase
  event_trigger: string
  event_type?: string
  properties: SegmentEventProperty[]
  destination?: string
}

interface SegmentEventObjectProp {
  key: string
  type: string
  limit?: number
  properties: SegmentEventProperty[]
  keyOverride?: string
}
type SegmentEventProperty = string | SegmentEventObjectProp
